import { useState, useRef, useEffect } from "react";
import ItineraryMap from "./ItineraryMap";
import EditableSection from "./EditableSection";

const EditableCityChip = ({ city, nights, days, type, onUpdate, onDelete, isEditMode }) => {
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({ city, nights, days });
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingField]);

  const handleSave = (field) => {
    setEditingField(null);
    const updates = { ...editValues };
    if (field === 'nights') {
      updates.nights = parseInt(updates.nights) || 1;
    }
    if (JSON.stringify(updates) !== JSON.stringify({ city, nights, days })) {
      onUpdate(updates);
    }
  };

  if (!isEditMode) {
    if (type === "departure") {
      return (
        <div className="flex flex-col p-3 bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-sky-200 rounded-xl transition-all min-w-[150px] relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-1 h-full bg-sky-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0">
              <span className="text-xs">✈️</span>
            </div>
            <span className="font-bold text-gray-900 text-[15px] truncate">{city}</span>
          </div>
          <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50">
            <span className="text-[10px] font-bold text-sky-700 bg-sky-50 px-2 py-1 rounded uppercase tracking-wider">
              Departure
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col p-3 bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-teal-200 rounded-xl transition-all min-w-[150px] relative overflow-hidden group">
        <div className="absolute top-0 left-0 w-1 h-full bg-teal-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
            <span className="text-xs">📍</span>
          </div>
          <span className="font-bold text-gray-900 text-[15px] truncate">{city}</span>
        </div>
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50">
          <span className="text-xs font-medium text-gray-500">
            {Number(nights) === 0 ? "Day Trip" : `${nights} ${Number(nights) === 1 ? 'Night' : 'Nights'}`}
          </span>
          {days && (
            <span className="text-[10px] font-bold text-teal-700 bg-teal-50 px-2 py-1 rounded uppercase tracking-wider">
              {days}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-50 to-blue-50 rounded-full border border-teal-100 group">
      <span className="text-lg">📍</span>

      {editingField === 'city' ? (
        <input
          ref={inputRef}
          type="text"
          value={editValues.city}
          onChange={(e) => setEditValues(prev => ({ ...prev, city: e.target.value }))}
          onBlur={() => handleSave('city')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave('city');
            if (e.key === 'Escape') {
              setEditValues(prev => ({ ...prev, city }));
              setEditingField(null);
            }
          }}
          className="w-24 px-1 py-0.5 text-sm border border-teal-400 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
      ) : (
        <span
          className="font-semibold text-gray-800 cursor-pointer hover:bg-white/50 rounded px-1"
          onClick={() => setEditingField('city')}
        >
          {city}
        </span>
      )}

      <span className="text-gray-400">•</span>

      {editingField === 'nights' ? (
        <input
          ref={inputRef}
          type="number"
          min="1"
          value={editValues.nights}
          onChange={(e) => setEditValues(prev => ({ ...prev, nights: e.target.value }))}
          onBlur={() => handleSave('nights')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave('nights');
            if (e.key === 'Escape') {
              setEditValues(prev => ({ ...prev, nights }));
              setEditingField(null);
            }
          }}
          className="w-12 px-1 py-0.5 text-sm border border-teal-400 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-center"
        />
      ) : (
        <span
          className="text-teal-700 font-medium cursor-pointer hover:bg-white/50 rounded px-1"
          onClick={() => setEditingField('nights')}
        >
          {Number(nights) === 0 ? "Day Trip" : `${nights} ${Number(nights) === 1 ? 'Night' : 'Nights'}`}
        </span>
      )}

      <button
        onClick={onDelete}
        className="ml-1 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
        title="Remove city"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const TripOverviewSection = ({
  tripSummary,
  tripData,
  onUpdateOverview,
  onUpdateCitiesBreakdown,
  cachedMapData,
  onMapDataGenerated,
  originalOverview,
  originalCitiesBreakdown
}) => {
  const [isEditingCities, setIsEditingCities] = useState(false);

  if (!tripSummary?.overview) return null;

  const citiesHaveChanges = JSON.stringify(tripSummary.citiesBreakdown) !== JSON.stringify(originalCitiesBreakdown);

  const handleAddCity = () => {
    const newCity = {
      city: "New City",
      nights: 1,
      days: ""
    };
    const updated = [...(tripSummary.citiesBreakdown || []), newCity];
    onUpdateCitiesBreakdown && onUpdateCitiesBreakdown(updated);
  };

  const handleUpdateCity = (index, updates) => {
    const updated = [...(tripSummary.citiesBreakdown || [])];
    updated[index] = { ...updated[index], ...updates };
    onUpdateCitiesBreakdown && onUpdateCitiesBreakdown(updated);
  };

  const handleDeleteCity = (index) => {
    const updated = (tripSummary.citiesBreakdown || []).filter((_, i) => i !== index);
    onUpdateCitiesBreakdown && onUpdateCitiesBreakdown(updated);
  };

  const handleRevertOverview = () => {
    const originalValue = originalOverview || tripSummary.overview;
    onUpdateOverview && onUpdateOverview(originalValue);
  };

  const handleRevertCities = () => {
    if (window.confirm("Are you sure you want to revert cities to original? Your changes will be lost.")) {
      onUpdateCitiesBreakdown && onUpdateCitiesBreakdown(originalCitiesBreakdown || []);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8 mb-6 border border-gray-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-teal-400 to-teal-600 rounded-l-2xl"></div>
      
      <div className="mb-6 pb-6 border-b border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex flex-wrap items-center gap-3">
          {tripSummary.tripTitle || `Explore ${tripSummary.destination || tripData?.destination}`}
          <span className="text-sm font-medium border border-gray-300 rounded px-2.5 py-0.5 text-gray-700 bg-gray-50 shadow-sm">
            {tripSummary.duration?.replace(' Nights', 'N').replace(' Days', 'D').replace(' / ', '/') || "N/A"}
          </span>
        </h2>
        {tripSummary.citiesBreakdown?.length > 0 && (
          <p className="text-gray-600 text-[15px] leading-relaxed flex flex-wrap gap-x-2 gap-y-1 items-center font-medium">
            {tripSummary.citiesBreakdown
              .filter((item, idx, arr) => {
                if (item.type === 'theme' || item.city.toLowerCase() === 'presidency') return false;
                // Filter out the automatically added arrival/departure gateways if they are a 0N stay
                if ((item.type === 'arrival' || item.type === 'departure') && (item.nights == 0 || item.nights == '0')) {
                  return false;
                }
                return true;
              })
              .map((item, idx, filteredArr) => (
              <span key={idx} className="whitespace-nowrap flex items-center">
                {item.nights}N {item.city}
                {idx < filteredArr.length - 1 && <span className="mx-2 text-gray-400 text-xl leading-none">•</span>}
              </span>
            ))}
          </p>
        )}
      </div>

      <EditableSection
        title="About This Trip"
        value={tripSummary.overview}
        originalValue={originalOverview}
        onSave={(newValue) => onUpdateOverview && onUpdateOverview(newValue)}
        onRevert={handleRevertOverview}
        type="textarea"
        rows={4}
        placeholder="Enter trip overview..."
        viewClassName="text-[15px] sm:text-base text-gray-600 leading-relaxed tracking-wide"
      />


      <div className="mt-6 pt-6 border-t border-gray-100">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Your Journey</h3>
        <ItineraryMap
          citiesBreakdown={tripSummary.citiesBreakdown}
          startingFrom={tripSummary.startingFrom}
          destination={tripSummary.destination}
          destinationData={tripData?.destinationData}
          travelModes={tripData?.travelBetween || tripData?.travelToDest}
          cachedMapData={cachedMapData}
          onMapDataGenerated={onMapDataGenerated}
          isMultiDest={Array.isArray(tripData?.destinations) && tripData.destinations.length >= 2}
          userDestinations={tripData?.destinations}
          startingLocationData={tripData?.startingLocationData}
        />
      </div>
    </div>
  );
};

export default TripOverviewSection;
